import { Component, HostListener, OnInit, inject } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { Subject, debounceTime } from 'rxjs';
import { MainLayoutComponent } from '../../_layouts/main-layout/main-layout.component';
import { ConfirmDialogComponent } from '../../../Shared/_components/confirm-dialog/confirm-dialog.component';
import { AlertService } from '../../_services/alert.service';
import { LandscaperService } from '../../_services/landscaper.service';
import { Landscaper, LandscaperStats } from '../../_interfaces/landscaper';
import { PaginatorPagesDirective } from '../../../Shared/_directives/paginator-pages.directive';

// The list as it stands, carried to a landscaper's page and back.
export interface LandscaperListState {
  q?: string;
  status?: string;
  sort?: string;
  page?: string;
  size?: string;
}

@Component({
    selector: 'app-landscapers',
    imports: [PaginatorPagesDirective, CommonModule, MainLayoutComponent, FormsModule, MatIconModule, MatTooltipModule, MatPaginatorModule, MatProgressSpinnerModule],
    templateUrl: './landscapers.component.html',
    styleUrl: './landscapers.component.css'
})
export class LandscapersComponent implements OnInit {
  private readonly dialog = inject(MatDialog);
  private readonly alertService = inject(AlertService);
  private readonly landscaperService = inject(LandscaperService);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly sanitizer = inject(DomSanitizer);

  // The slide-over panel, with an embedded map of the pin.
  selected: Landscaper | null = null;
  selectedMap: SafeResourceUrl | null = null;

  landscapers: Landscaper[] = [];
  count = 0;
  stats: LandscaperStats | null = null;
  pageNumber = 0;
  itemsPerPage = 10;
  selectedStatus = 'all';
  keyword = '';
  sort = 'default';
  loading = true;
  loadError = false;
  busyId = 0;

  readonly statusOptions = [
    { value: 'all', label: 'All' },
    { value: 'pending', label: 'Awaiting approval' },
    { value: 'approved', label: 'Live on website' },
    { value: 'nolocation', label: 'Not on the map' },
  ];

  private readonly search$ = new Subject<void>();
  private loadSeq = 0;

  constructor() {
    this.search$.pipe(debounceTime(350), takeUntilDestroyed()).subscribe(() => {
      this.pageNumber = 0;
      this.load();
    });
  }

  ngOnInit(): void {
    // Back from a landscaper's page: the list as it was.
    const qp = this.route.snapshot.queryParamMap;
    if (qp.get('q')) this.keyword = qp.get('q')!;
    if (qp.get('status')) this.selectedStatus = qp.get('status')!;
    if (qp.get('sort')) this.sort = qp.get('sort')!;
    if (qp.get('page')) this.pageNumber = Math.max(0, Number(qp.get('page')) || 0);
    if (qp.get('size')) this.itemsPerPage = Math.max(1, Number(qp.get('size')) || 10);
    this.load();
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    this.selected = null;
  }

  get listState(): LandscaperListState {
    const s: LandscaperListState = {};
    if (this.keyword.trim()) s.q = this.keyword.trim();
    if (this.selectedStatus !== 'all') s.status = this.selectedStatus;
    if (this.sort !== 'default') s.sort = this.sort;
    if (this.pageNumber) s.page = String(this.pageNumber);
    if (this.itemsPerPage !== 10) s.size = String(this.itemsPerPage);
    return s;
  }

  view(l: Landscaper): void {
    if (this.selected?.storeId === l.storeId) {
      this.selected = null;
      return;
    }
    this.selected = l;
    this.selectedMap = l.hasLocation
      ? this.sanitizer.bypassSecurityTrustResourceUrl(`https://maps.google.com/maps?q=${encodeURIComponent(l.latitude + ',' + l.longitude)}&z=13&output=embed`)
      : null;
  }

  get filtersActive(): boolean {
    return this.selectedStatus !== 'all' || !!this.keyword;
  }

  countFor(value: string): number {
    const s = this.stats;
    if (!s) return 0;
    switch (value) {
      case 'pending': return s.pending;
      case 'approved': return s.approved;
      case 'nolocation': return s.noLocation;
      default: return s.total;
    }
  }

  load(): void {
    const seq = ++this.loadSeq;
    this.loading = true;
    this.landscaperService
      .getLandscapers({
        pageIndex: this.pageNumber,
        pageSize: this.itemsPerPage,
        selectedStatus: this.selectedStatus,
        keyword: this.keyword.trim(),
        sort: this.sort,
      })
      .subscribe({
        next: (response: any) => {
          if (seq !== this.loadSeq) return;
          this.landscapers = response.stores ?? [];
          this.count = response.count ?? 0;
          if (response.stats) this.stats = response.stats;
          this.loading = false;
          this.loadError = false;
        },
        error: (error) => {
          if (seq !== this.loadSeq) return;
          this.loading = false;
          this.loadError = true;
          this.alertService.alert(error.error?.message || 'Could not load landscapers', 'error');
        },
      });
  }

  onSearchInput(): void {
    this.search$.next();
  }

  clearSearch(): void {
    this.keyword = '';
    this.pageNumber = 0;
    this.load();
  }

  setStatus(value: string): void {
    this.selectedStatus = value;
    this.pageNumber = 0;
    this.load();
  }

  setSort(value: string): void {
    this.sort = value;
    this.pageNumber = 0;
    this.load();
  }

  resetFilters(): void {
    this.selectedStatus = 'all';
    this.keyword = '';
    this.pageNumber = 0;
    this.load();
  }

  onPage(event: PageEvent): void {
    if (this.itemsPerPage !== event.pageSize) {
      this.itemsPerPage = event.pageSize || 10;
      this.pageNumber = 0;
    } else {
      this.pageNumber = event.pageIndex;
    }
    this.load();
  }

  // ---- What a row shows ----

  initials(l: Landscaper): string {
    const words = (l.storeName ?? '').trim().split(/\s+/).filter(Boolean);
    return ((words[0]?.charAt(0) ?? '') + (words[1]?.charAt(0) ?? '')).toUpperCase() || '?';
  }

  // Address as one line, skipping the empty parts.
  addressLine(l: Landscaper): string {
    return [l.address1, l.address2, l.city, l.state, l.postCode]
      .map((p) => (p ?? '').trim())
      .filter(Boolean)
      .join(', ');
  }

  link(url: string | undefined | null): string {
    if (!url || url === '-') return '';
    return /^https?:\/\//i.test(url.trim()) ? url.trim() : `https://${url.trim()}`;
  }

  shortLink(url: string | undefined | null): string {
    return (url ?? '').trim().replace(/^https?:\/\/(www\.)?/i, '').replace(/\/$/, '');
  }

  mapLink(l: Landscaper): string {
    return `https://www.google.com/maps?q=${encodeURIComponent(l.latitude + ',' + l.longitude)}`;
  }

  // ---- Actions ----

  // Create / edit happen on the landscaper's own page.
  onEditClick(mode: string, editItem?: Landscaper): void {
    if (mode === 'EDIT' && editItem?.storeId) this.router.navigate(['/landscapers/edit', editItem.storeId], { queryParams: this.listState });
    else this.router.navigate(['/landscapers/create'], { queryParams: this.listState });
  }

  onApproveClick(l: Landscaper): void {
    this.busyId = l.storeId ?? 0;
    this.landscaperService.approveLandscraper(l).subscribe({
      next: () => this.afterApprove(),
      error: (error) => (LandscaperService.succeeded(error) ? this.afterApprove() : this.failed(error)),
    });
  }

  private afterApprove(): void {
    this.busyId = 0;
    this.alertService.alert('Landscaper approved - it is now on the website', 'success');
    if (this.selected) this.selected = { ...this.selected, isApproved: true };
    this.load();
  }

  private failed(error: any): void {
    this.busyId = 0;
    this.alertService.alert(error.error?.message || 'Something went wrong', 'error');
  }

  onDeleteClick(l: Landscaper): void {
    this.dialog
      .open(ConfirmDialogComponent, {
        width: '460px',
        maxWidth: '96vw',
        data: {
          title: 'Remove landscaper',
          message: `Remove ${(l.storeName ?? '').trim()}?`,
          detail: l.isApproved ? 'It disappears from the website\'s landscaper search straight away.' : 'The request is deleted.',
          confirmText: 'Remove',
        },
      })
      .afterClosed()
      .subscribe((result) => {
        if (!result?.confirmation || !l.storeId) return;
        this.busyId = l.storeId;
        this.landscaperService.deleteLandscaper(l.storeId).subscribe({
          next: () => {
            this.busyId = 0;
            this.alertService.alert('Landscaper removed', 'success');
            if (this.selected?.storeId === l.storeId) this.selected = null;
            this.load();
          },
          error: (error) => this.failed(error),
        });
      });
  }
}
